import { inject } from '@angular/core';
import {
  HttpRequest,
  HttpEvent,
  HttpErrorResponse,
  HttpResponse,
  HttpInterceptorFn,
  HttpHandlerFn
} from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '@core/auth.service';
import { Router } from '@angular/router';
import { Application } from '@enums/application.enums';
import { Location } from '@angular/common';
import { API_URLS } from './api-url.enum';
import { HttpService } from './http.service';
import { CommonService } from '@shared/services/common.service';
import { Fields, SERVER_HTTP_HEADERS, StatusCodes } from '@constants/constant';
import { appRoutingUrl } from '@enums/comman.enums';
import { environment } from '@environments/environment';
import { IndexedDbService } from '@services/indexed-db.service';

const refreshTokenSubject = new BehaviorSubject<string | null>(null);
let isRefreshing = false;

const POST_TYPES: Array<string> = [
  '/image/',
  '/video/',
  '/reel/',
  '/giveaway/',
  '/poll/',
  '/article/'
];

const ALLOWED_URLS: Array<string> = [API_URLS.pushNotification];

type ErrorInterceptorDeps = {
  router: Router;
  cs: CommonService;
  location: Location;
  authService: AuthService;
  httpService: HttpService;
  indexedDbService: IndexedDbService;
};

export const errorInterceptor: HttpInterceptorFn = (request, next) => {
  const deps: ErrorInterceptorDeps = {
    router: inject(Router),
    cs: inject(CommonService),
    location: inject(Location),
    authService: inject(AuthService),
    httpService: inject(HttpService),
    indexedDbService: inject(IndexedDbService)
  };

  const accessToken = deps.authService.accessToken?.value;
  if (accessToken) {
    request = request.clone({
      setHeaders: {
        Authorization: `${accessToken}`
      }
    });
  }
  if (!deps.cs.isBrowser && request.url.startsWith(environment.baseURL)) {
    request = request.clone({
      setHeaders: {
        [SERVER_HTTP_HEADERS.USER_AGENT_KEY]: SERVER_HTTP_HEADERS.USER_AGENT_VALUE,
        [SERVER_HTTP_HEADERS.INTERNAL_SOURCE_KEY]: SERVER_HTTP_HEADERS.INTERNAL_SOURCE_VALUE
      }
    });
  }
  return next(request).pipe(
    catchError((err: HttpErrorResponse) => {
      if (!deps.cs.isBrowser && err.status === Application.ZERO) {
        return EMPTY;
      }
      switch (err.status) {
        case StatusCodes.BAD_REQUEST:
          if (
            err?.error?.message?.toLowerCase() === Fields.NO_ACCESS_TO_CONTENT
          ) {
            if (shouldShowPrivateAccountOnPostDetail(request, err, deps.location)) {
              return of(
                new HttpResponse({
                  body: err.error,
                  status: StatusCodes.SUCCESS,
                  statusText: 'OK',
                  url: request.url
                })
              );
            }
            navigateToProfile(deps.location.path(), deps.router);
            return of(null);
          }
          break;
        case Application.FOUR_HUNDRED_ONE:
          return handle401Error(request, next, deps);
        case Application.FOUR_HUNDRED_THREE:
          // Prerender/SSR has no user session; logging out would navigate and bake a redirect.
          if (!deps.cs.isBrowser) {
            break;
          }
          deps.authService.removeDetails();
          deps.cs.logoutUser();
          updateProfileTheme(Application.THEME_DARK, deps.cs);
          navigateToLogin(deps);
          setTimeout(() => {
            deps.cs.updateModuleLoaderStatus(true);
          }, Application.INTERVAL_THREE_THOUSAND);
          break;
      }
      const error = err.error;
      if (err.status === Application.FOUR_HUNDRED_ONE) {
        return throwError(error);
      } else {
        return throwError(error);
      }
    })
  );
};

function handle401Error(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  deps: ErrorInterceptorDeps
): Observable<HttpEvent<unknown>> {
  if (!deps.cs.isBrowser) {
    // Prerender/SSR has no user session. Complete quietly so Angular
    // does not log an unhandled "Unauthorized" error for public pages.
    return EMPTY;
  }
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);
    const refreshToken = (deps.cs.isBrowser ? JSON.parse(localStorage.getItem(Application.REFRESH_TOKEN)) : null) || deps.indexedDbService.getValueFromIndexedDb(Application.REFRESH_TOKEN);
    if (refreshToken) {
      const savedRefreshToken = refreshToken;

      return callRefreshToken(
        API_URLS.refreshToken,
        { refreshToken: savedRefreshToken.fm_over },
        false,
        deps.httpService
      ).pipe(
        switchMap((res: any) => {
          refreshTokenSubject.next(res.token);
          deps.authService.changeToken({ fm_user: res.token });
          setTimeout(() => {
            deps.cs.updateModuleLoaderStatus(true);
          }, Application.INTERVAL_THREE_THOUSAND);
          isRefreshing = false;
          return next(addTokenHeader(request, res.token));
        }),
        catchError((err) => {
          isRefreshing = false;
          updateProfileTheme(Application.THEME_DARK, deps.cs);
          deps.authService.removeDetails();
          deps.cs.logoutUser();
          navigateToLogin(deps);
          deps.cs.showErrorMessage(Application.AUTH_EXPIRED);
          return throwError(() => err);
        })
      );
    } else {
      if (
        !ALLOWED_URLS.includes(
          request.url.replace(environment.baseURL, '')
        )
      ) {
        updateProfileTheme(Application.THEME_DARK, deps.cs);
        deps.cs.showErrorMessage(Application.AUTH_EXPIRED);
        isRefreshing = false;
        deps.authService.removeDetails();
        deps.cs.logoutUser();
        navigateToLogin(deps);
      }
    }
  }

  return refreshTokenSubject.pipe(
    filter((token) => token !== null),
    take(1),
    switchMap((token) => next(addTokenHeader(request, token!)))
  );
}

function isSubstringPresentInURL(
  currentBrowserURL: string,
  subString: string
): boolean {
  return currentBrowserURL.includes(subString);
}

function isPostDetailUrl(currentBrowserURL: string): boolean {
  return isSubstringPresentInURL(
    currentBrowserURL,
    appRoutingUrl.TYPE_POST
  );
}

function shouldShowPrivateAccountOnPostDetail(
  request: HttpRequest<unknown>,
  err: HttpErrorResponse,
  location: Location
): boolean {
  return (
    !!err?.error?.is_private &&
    request.url.includes('post/post_details') &&
    isPostDetailUrl(location.path())
  );
}

function getUserName(
  currentBrowserURL: string,
  separator: string,
  index: number
): string {
  return currentBrowserURL.split(separator)?.[index];
}

function navigateToProfile(currentBrowserURL: string, router: Router) {
  let username;
  if (
    isSubstringPresentInURL(
      currentBrowserURL,
      appRoutingUrl.COURSE_DETAIL
    )
  ) {
    username = getUserName(currentBrowserURL, '?username=', 1);
  } else if (
    isSubstringPresentInURL(currentBrowserURL, appRoutingUrl.POST_FORUM)
  ) {
    username = getUserName(currentBrowserURL, '/', 3);
  } else if (
    isSubstringPresentInURL(
      currentBrowserURL,
      appRoutingUrl.TYPE_POST
    ) &&
    POST_TYPES.some((substring) =>
      isSubstringPresentInURL(currentBrowserURL, substring)
    )
  ) {
    username = getUserName(currentBrowserURL, '/', 2);
  }
  if (username) {
    router.navigate(['', username]);
  } else return;
}

function updateProfileTheme(selectedTheme: string, cs: CommonService): void {
  cs.updateTheme(selectedTheme);
}

function callRefreshToken(endpoint: string, data, loader, httpService: HttpService): Observable<any> {
  return httpService.httpRequest(`PUT`, endpoint, loader, data);
}

function addTokenHeader(request: HttpRequest<unknown>, token: string) {
  return request.clone({
    headers: request.headers.set('Authorization', `${String(token)}`)
  });
}

function navigateToLogin(deps: ErrorInterceptorDeps) {
  if (deps.cs.isWhiteLabelWeb) {
    const username = deps.cs.whiteLabelData.user.user_name;
    deps.router.navigate([`/${username}`]);
    return;
  }
  deps.router.navigate(['/']);
}
